/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useState } from 'react';
import {
  isObjEmptyOrNull,
  getErrorFromEndpoint,
  isStrEmptyOrNull,
  convertDateToUTCString,
  formatAsDatabaseDate,
  addToObj,
} from '../UtilityFunctions';
import { AxiosError } from 'axios';
import { useLoanContext } from '../../contexts/LoanContext';
import useEventDateList from './useEventDateList';
import useBatch from './useBatch';

// AEA = Aggregate Escrow Analysis History
// this allows servicers to see what applied escrow analyses have been done for the loan (from the escrow analysis page)
// this is used in the Escrow analysis page to see current or past aea analyses done
export function useAEAHistory() {
  const { loanNumber } = useLoanContext();
  const [AEAHistory, setAEAHistory] = useState<UnknownAEAHistoryResultObj>(null);
  const [errors, setErrors] = useState('');
  const [loading, setLoading] = useState(false);

  const { getBatch } = useBatch();

  // the event date list hook is used to get all occurrences for the aea analyses done - event date AEA
  const { getEventDateList, getEventCodeSpecificOccurrence } = useEventDateList();

  // aea history uses the following endpoints to get the history depending on the date from the occurrence requested
  const getBatchParams = useCallback(
    async (dateToCheck: string): Promise<AEAHistoryBatchReq> => {
      const req: AEAHistoryBatchReq = {
        createOrUpdatePayment: {
          endpoint: process.env.REACT_APP_PAYMENT_DUEDATE.replace('live/', '').replace('?', loanNumber) + dateToCheck,
        },
        impoundTotal: {
          endpoint: process.env.REACT_APP_IMPOUND_TOTAL.replace('live/', '').replace('?', loanNumber) + dateToCheck,
        },
        // the trial balances endpoint shows the trial balances that were present at the the time the aea occurrence was done
        trialBalances: {
          endpoint:
            process.env.REACT_APP_ESCROW_MONTHLY_TRIAL_BALANCES.replace('live/', '').replace('?', loanNumber) +
            dateToCheck +
            '/list',
        },
      };

      return req;
    },
    [loanNumber],
  );

  // this function gets the date which we use to get the data from the other endpoints,
  // aka the date the escrow analysis was applied for whichever occurrence we're working with
  // the date will be in the format the endpoints accept = yyyy/mm/dd (year, month, day)
  // the event object for the occurrence has the activeDate variable, which is what we will use
  const getDatabaseDateToCheck = useCallback((eventObj: EventObj | null) => {
    if (isObjEmptyOrNull(eventObj)) return '';
    if (!('activeDate' in eventObj)) return '';
    let utcdate = convertDateToUTCString(eventObj.activeDate * 1000);
    let utcdatearr = utcdate.split('/'); // mm/dd/year
    let databasedate = formatAsDatabaseDate(utcdatearr[2], utcdatearr[0], utcdatearr[1]);
    return databasedate;
  }, []);

  const errorInBatchRes = useCallback((res) => {
    let errorStr = '';
    for (const endpoint in res) {
      const err = getErrorFromEndpoint(res, endpoint);
      if (!isStrEmptyOrNull(err)) {
        errorStr += err + '. ';
      }
    }
    if (isStrEmptyOrNull(errorStr)) return false;
    console.log('Error in AEAHistoryAPI: ' + errorStr);
    setErrors(errorStr);
    return true;
  }, []);

  const getBatchData = useCallback(
    async (databasedate: string): Promise<UnknownAEAHistoryBatchRes> => {
      // get data from endpoints for the date
      const batchParams = await getBatchParams(databasedate);
      const batchRes = await getBatch(batchParams);

      if (isObjEmptyOrNull(batchRes)) return null;
      const errorInRes = errorInBatchRes(batchRes);
      if (errorInRes) return null;
      return batchRes as UnknownAEAHistoryBatchRes;
    },
    [getBatchParams, getBatch, errorInBatchRes],
  );

  /*
    occur is the number starting at 0, meaning current
    gets AEA events for loan, then gets the relevant AEA event, the date it was applied
    using the date, the data from the relevant endpoints is returned
    */
  const AEAHistoryAPI = useCallback(
    async (occur: number): Promise<UnknownAEAHistoryResultObj> => {
      const eventDateObj: EventObj | null = await getEventCodeSpecificOccurrence(occur, 'AEA');
      if (isObjEmptyOrNull(eventDateObj)) return null;

      const aeaRunDateObj: EventObj | null = await getEventCodeSpecificOccurrence(occur, 'AER');

      // get the date AEA was applied from the AEA event
      const databasedate = getDatabaseDateToCheck(eventDateObj);
      if (isStrEmptyOrNull(databasedate)) return null;

      const batchRes = await getBatchData(databasedate);
      if (isObjEmptyOrNull(batchRes)) return null;

      const res = addToObj(batchRes, [{ AEAEvent: eventDateObj, AEARunDateEvent: aeaRunDateObj }]);
      return res as AEAHistoryResultObj;
    },
    [getEventCodeSpecificOccurrence, getBatchData, getDatabaseDateToCheck],
  );

  /* aea history gets the applied aea if present for the requested occurrence - past or current
  current means the event occurrence can be 00, which gets the latest occurrence
  past means the occurrence before the latest one
  for instance if the 00 occurrence gets 02, meaning the latest occurrence is 02, the past one will be 01
  occur is 0 for current and 1 for past, the lower the number is, the more recent it is
  */
  const getAEAHistory = useCallback(
    async (occur: number): Promise<UnknownAEAHistoryResultObj> => {
      setErrors('');
      setLoading(true);
      const res = await AEAHistoryAPI(occur)
        .then((res) => {
          setAEAHistory(res);
          return res;
        })
        .finally(() => {
          setLoading(false);
        });
      return res;
    },
    [AEAHistoryAPI],
  );

  return {
    AEAHistory,

    AEAHistoryLoading: loading,
    AEAHistoryErrors: errors,

    getAEAHistory,
  };
}

export default useAEAHistory;
